import { Box, Button, Portal, Snackbar } from "@mui/material";
import { OutreachSendContext } from "contexts/OutreachSend";
import { ReactNode, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { routes } from "schemas/routes";

import { trackEvent } from "utils/tracking";

const SENT_AUTOHIDE_MS = 5000;
const FAILED_AUTOHIDE_MS = 8000;
const UNDONE_AUTOHIDE_MS = 3000;

const OutreachSendSnackbar = () => {
  const { state, undo, dismiss } = useContext(OutreachSendContext);

  if (!state) return null;

  let message: ReactNode = null;
  let action: ReactNode = null;
  let autoHide: number | null = null;

  if (state.status === "sending") {
    message = `Scheduling email to ${state.contactEmail}`;
    action = (
      <Button
        color="inherit"
        size="small"
        onClick={undo}
        sx={{ fontWeight: 600 }}
      >
        UNDO
      </Button>
    );
  } else if (state.status === "processing") {
    message = "Processing...";
  } else if (state.status === "sent") {
    message = `Email scheduled for ${state.contactEmail}`;
    autoHide = SENT_AUTOHIDE_MS;
    const viewHref = state.outreachContactId
      ? `/${routes.inboxScheduled}/${state.outreachContactId}?fromSend=true`
      : `/${routes.inboxScheduled}`;
    action = (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Button
          color="inherit"
          size="small"
          component={RouterLink}
          to={viewHref}
          onClick={() => {
            trackEvent("Outreach Send Snackbar View Clicked", {
              "Is Scheduled": state.isScheduled,
              "Has Outreach Contact Id": Boolean(state.outreachContactId),
            });
            dismiss();
          }}
          sx={{ fontWeight: 600 }}
        >
          VIEW
        </Button>
        <Button color="inherit" size="small" onClick={dismiss}>
          DISMISS
        </Button>
      </Box>
    );
  } else if (state.status === "failed") {
    const verb = state.isScheduled ? "schedule" : "send";
    message = state.errorMessage
      ? `Failed to ${verb}: ${state.errorMessage}`
      : `Failed to ${verb} email`;
    autoHide = FAILED_AUTOHIDE_MS;
    action = (
      <Button color="inherit" size="small" onClick={dismiss}>
        DISMISS
      </Button>
    );
  } else if (state.status === "undone") {
    message = "Send cancelled";
    autoHide = UNDONE_AUTOHIDE_MS;
  }

  return (
    <Portal>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          dismiss();
        }}
        autoHideDuration={autoHide ?? null}
        message={message}
        action={action}
        ContentProps={{
          sx: {
            backgroundColor: "primary.main",
            color: "white",
            minWidth: { xs: "90vw", sm: 360 },
            fontSize: 14,
          },
        }}
        sx={{ zIndex: 2000 }}
      />
    </Portal>
  );
};

export default OutreachSendSnackbar;
